import React, { useState } from "react";
import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const PayrollStructuresTab = ({
  components,
  structures,
  saving,
  saveStructure,
  deleteStructure,
  copyVersion,
}) => {
  const [form] = Form.useForm();
  const [copyForm] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [copySource, setCopySource] = useState(null);

  const showForm = (structure = null) => {
    setSelected(structure);
    form.resetFields();
    form.setFieldsValue(
      structure
        ? {
            ...structure,
            effectiveFrom: structure.effectiveFrom
              ? dayjs(structure.effectiveFrom)
              : null,
            effectiveTo: structure.effectiveTo
              ? dayjs(structure.effectiveTo)
              : null,
            componentIds: structure.structureComponents?.map(
              (item) => item.componentId,
            ),
          }
        : { isActive: true, effectiveFrom: dayjs() },
    );
    setOpen(true);
  };

  const submit = async () => {
    const values = await form.validateFields();
    const payload = {
      ...values,
      effectiveFrom: values.effectiveFrom?.format("YYYY-MM-DD") || null,
      effectiveTo: values.effectiveTo?.format("YYYY-MM-DD") || null,
      structureComponents: values.componentIds.map((componentId, index) => ({
        componentId,
        sortOrder: index,
        isRequired: true,
      })),
    };
    delete payload.componentIds;
    const result = await saveStructure(selected?.structureId, payload);
    if (result) setOpen(false);
  };

  const submitCopy = async () => {
    const values = await copyForm.validateFields();
    const result = await copyVersion(copySource.structureId, {
      effectiveFrom: values.effectiveFrom.format("YYYY-MM-DD"),
      effectiveTo: values.effectiveTo?.format("YYYY-MM-DD") || null,
    });
    if (result) setCopySource(null);
  };

  const columns = [
    {
      title: "Salary structure",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <strong>{row.name}</strong>
          <span style={{ color: "#8c8c8c" }}>
            {row.code} · v{row.versionNumber}
          </span>
        </Space>
      ),
    },
    {
      title: "Employee category",
      dataIndex: "employeeCategory",
      render: (value) => value || "All categories",
    },
    {
      title: "Effective",
      render: (_, row) =>
        `${row.effectiveFrom ? dayjs(row.effectiveFrom).format("DD MMM YYYY") : "Any date"} — ${row.effectiveTo ? dayjs(row.effectiveTo).format("DD MMM YYYY") : "Open"}`,
    },
    {
      title: "Components",
      render: (_, row) => (
        <Space wrap>
          {row.structureComponents?.map((item) => (
            <Tag key={item.componentId}>{item.componentName}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (value) => (
        <Tag color={value ? "success" : "default"}>
          {value ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      width: 130,
      align: "center",
      render: (_, row) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => showForm(row)}
            />
          </Tooltip>
          <Tooltip title="Copy as a new version">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => {
                setCopySource(row);
                copyForm.resetFields();
                copyForm.setFieldsValue({
                  effectiveFrom: dayjs().add(1, "day"),
                });
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this salary structure?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteStructure(row.structureId)}
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={saving}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <div style={{ textAlign: "right" }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => showForm()}
          disabled={!components.length}
        >
          Add structure
        </Button>
      </div>
      {!components.length && (
        <Tag color="warning">
          Create at least one component before creating a salary structure.
        </Tag>
      )}
      <Table
        rowKey="structureId"
        columns={columns}
        dataSource={structures}
        scroll={{ x: 760 }}
      />

      <Modal
        title={selected ? "Edit salary structure" : "Add salary structure"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={submit}
        confirmLoading={saving}
        okText="Save structure"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Structure name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="employeeCategory" label="Employee category">
            <Select
              allowClear
              options={["Teaching", "Support", "Administration"].map(
                (value) => ({ value, label: value }),
              )}
            />
          </Form.Item>
          <Space style={{ width: "100%" }} align="start">
            <Form.Item name="effectiveFrom" label="Effective from">
              <DatePicker />
            </Form.Item>
            <Form.Item name="effectiveTo" label="Effective to">
              <DatePicker />
            </Form.Item>
          </Space>
          <Form.Item
            name="componentIds"
            label="Included components"
            rules={[{ required: true, type: "array", min: 1 }]}
          >
            <Select
              mode="multiple"
              optionFilterProp="label"
              options={components
                .filter(
                  (item) =>
                    item.isActive &&
                    (!item.effectiveFrom ||
                      !dayjs(item.effectiveFrom).isAfter(dayjs(), "day")) &&
                    (!item.effectiveTo ||
                      !dayjs(item.effectiveTo).isBefore(dayjs(), "day")),
                )
                .map((item) => ({
                  value: item.componentId,
                  label: `${item.name} (${item.code} v${item.versionNumber})`,
                }))}
            />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={
          copySource
            ? `Copy ${copySource.code} v${copySource.versionNumber}`
            : "Copy structure"
        }
        open={Boolean(copySource)}
        onCancel={() => setCopySource(null)}
        onOk={submitCopy}
        confirmLoading={saving}
        okText="Create new version"
      >
        <Form form={copyForm} layout="vertical">
          <Form.Item
            name="effectiveFrom"
            label="New version effective from"
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="effectiveTo" label="New version effective to">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default PayrollStructuresTab;
